/**
 * Stale / expiry helpers for feature flags and overrides.
 */

import { parseBackendDateTime } from '@/lib/format/datetime';
import type { FlagResponse } from '@/types/featureFlags';

const DAY_MS = 24 * 60 * 60 * 1000;
const SOON_DAYS = 14;

export type ExpiryStatus = 'none' | 'ok' | 'soon' | 'expired';

export interface ExpiryInfo {
  status: ExpiryStatus;
  daysLeft: number | null;
}

export function getExpiryInfo(expiresAt?: string | null): ExpiryInfo {
  if (!expiresAt) return { status: 'none', daysLeft: null };
  const expiry = parseBackendDateTime(expiresAt).getTime();
  const now = Date.now();
  const daysLeft = Math.ceil((expiry - now) / DAY_MS);
  if (expiry <= now) return { status: 'expired', daysLeft };
  if (expiry <= now + SOON_DAYS * DAY_MS) return { status: 'soon', daysLeft };
  return { status: 'ok', daysLeft };
}

/** A flag is stale when active and its expiry is expired or expiring soon. */
export function isFlagStale(flag: FlagResponse): boolean {
  if (flag.lifecycle_state !== 'active') return false;
  const { status } = getExpiryInfo(flag.expires_at);
  return status === 'expired' || status === 'soon';
}

export function getStaleFlags(flags: FlagResponse[]): FlagResponse[] {
  return flags.filter(isFlagStale);
}
